import { apiJson } from '@/lib/http';

interface ApiResponse<T> {
  success?: boolean;
  message?: string;
  data?: T;
}

export interface BusinessServiceItemDto {
    id?: string;
    name: string;
    price: string;
    duration: string;
    category: string;
    inStock: boolean;
    description?: string;
}

export interface BusinessHourDto {
    enabled: boolean;
    open: string;
    close: string;
}

export interface BusinessFaqDto {
    id?: string;
    question: string;
    answer: string;
}

export interface ClosedDate {
    date: string; // ISO string for frontend date
    reason: string;
}

export interface BusinessInfoDto {
    id?: string;
    ownerName?: string;
    businessName?: string;
    businessCategory?: string;
    businessPhone?: string;
    address?: string;
    website?: string;
    email?: string;
    about?: string;
    parkingInfo?: string;
    accessibilityNotes?: string;
    directions?: string;
    suiteInstructions?: string;
    deliveryRadius?: number;
    deliveryRadiusUnit?: string;
    deliveryFee?: number;
    acceptedPaymentTypes?: string[];
    requireDeposit?: boolean;
    depositAmount?: number;
    cancellationFee?: number;
    noShowFee?: number;
    closedDates?: ClosedDate[];
    services?: BusinessServiceItemDto[];
    products?: BusinessServiceItemDto[];
    hours?: Record<string, BusinessHourDto>;
    faqs?: BusinessFaqDto[];
}

const apiCall = async <T>(endpoint: string, options: RequestInit = {}) => apiJson<ApiResponse<T> | T>(endpoint, options);
const unwrap = <T>(res: ApiResponse<T> | T): T => (res as ApiResponse<T>)?.data ?? (res as T);

export const businessService = {
  async getBusinessInfo(): Promise<BusinessInfoDto> {
    const res = await apiCall<BusinessInfoDto>('/Business');
    return unwrap(res) || {};
  },

  async updateBusinessInfo(info: BusinessInfoDto): Promise<BusinessInfoDto> {
    const res = await apiCall<BusinessInfoDto>('/Business/info', {
        method: 'POST',
        body: JSON.stringify(info)
    });
    return unwrap(res);
  },

  async updateBusinessHours(hours: Record<string, BusinessHourDto>): Promise<BusinessInfoDto> {
    const res = await apiCall<BusinessInfoDto>('/Business/hours', {
        method: 'POST',
        body: JSON.stringify(hours)
    });
    return unwrap(res);
  },

  async updateBusinessFaqs(faqs: BusinessFaqDto[]): Promise<BusinessInfoDto> {
    const res = await apiCall<BusinessInfoDto>('/Business/faqs', {
        method: 'POST',
        body: JSON.stringify(faqs)
    });
    return unwrap(res);
  },

  async addService(service: BusinessServiceItemDto): Promise<BusinessServiceItemDto> {
    const res = await apiCall<BusinessServiceItemDto>('/Business/services', {
        method: 'POST',
        body: JSON.stringify(service)
    });
    return unwrap(res);
  },

  async updateService(id: string, service: BusinessServiceItemDto): Promise<BusinessServiceItemDto> {
    const res = await apiCall<BusinessServiceItemDto>(`/Business/services/${id}`, {
        method: 'PUT',
        body: JSON.stringify(service)
    });
    return unwrap(res);
  },

  async deleteService(id: string): Promise<void> {
    await apiCall(`/Business/services/${id}`, { method: 'DELETE' });
  },

  async addProduct(product: BusinessServiceItemDto): Promise<BusinessServiceItemDto> {
    const res = await apiCall<BusinessServiceItemDto>('/Business/products', {
        method: 'POST',
        body: JSON.stringify(product)
    });
    return unwrap(res);
  },

  async updateProduct(id: string, product: BusinessServiceItemDto): Promise<BusinessServiceItemDto> {
    const res = await apiCall<BusinessServiceItemDto>(`/Business/products/${id}`, {
        method: 'PUT',
        body: JSON.stringify(product)
    });
    return unwrap(res);
  },

  async deleteProduct(id: string): Promise<void> {
    await apiCall(`/Business/products/${id}`, { method: 'DELETE' });
  },

  async bulkImportProducts(rawText: string): Promise<BusinessServiceItemDto[]> {
    const res = await apiCall<BusinessServiceItemDto[]>('/Business/products/bulk-import', {
        method: 'POST',
        body: JSON.stringify({ rawText })
    });
    return unwrap(res) || [];
  }
};

